<template>
  <div
    :class="{dark: isDark, [size]: size}"
    class="meta-info"
  >
    <h2
      ref="podHeading"
      :class="['meta-info-pod type-s', {scrolling: podScrolling}]"
      @mouseenter="setPodScrolling"
      @mouseleave="podScrolling = false">
      <span class="meta-info-static">{{ podTitle === '' ? '&nbsp;' : podTitle }}</span>
      <span
        ref="podText"
        aria-hidden="true"
        class="meta-info-ticker">
        {{ podTitle === '' ? '&nbsp;' : podTitle }}
      </span>
      <span
        aria-hidden="true"
        class="meta-info-ticker meta-info-ticket-secondary">
        {{ podTitle === '' ? '&nbsp;' : podTitle }}
      </span>
    </h2>
    <h1
      ref="epHeading"
      :class="['meta-info-epi type-xl-wide', {scrolling: epScrolling}]"
      @mouseenter="setEpScrolling"
      @mouseleave="epScrolling = false">
      <span class="meta-info-static">{{ epiTitle === '' ? '&nbsp;' : epiTitle }}</span>
      <span
        ref="epText"
        aria-hidden="true"
        class="meta-info-ticker">
        {{ epiTitle === '' ? '&nbsp;' : epiTitle }}
      </span>
      <span
        aria-hidden="true"
        class="meta-info-ticker meta-info-ticket-secondary">
        {{ epiTitle === '' ? '&nbsp;' : epiTitle }}
      </span>
    </h1>
    <div class="powered-by">
      Powered by <a
        title="Simplecast"
        href="https://simplecast.com"
        target="_blank">Simplecast</a>
    </div>
  </div>
</template>

<script>

export default {
  props: {
    store: {
      type: Object,
      default: function () {}
    }
  },
  data () {
    return {
      epScrolling: false,
      podScrolling: false
    }
  },
  computed: {
    epi () { return this.store.state.episode },
    epiTitle () {
      if (this.epi) return this.epi.title
      if (!this.isLoaded) return ''

      return 'Audio Not Found'
    },
    isDark () { return this.store.state.isDark },
    isLoaded () { return this.store.state.isLoaded },
    pod () { return this.store.state.podcast },
    podTitle () {
      let title = this.pod.title

      if (title) {
        if (this.seasonEpisode) {
          title = `${title} | ${this.seasonEpisode}`
        }

        return title
      }

      if (!this.isLoaded) return ''
      if (!title) return '404'
      return ''
    },
    seasons () { return this.store.state.seasons },
    seasonEpisode () {
      const ep = this.epi.number > -1 && this.epi.number !== null ? `EP${this.epi.number}` : null
      const s = this.pod.type !== 'episodic' && this.epi.season && this.epi.season.number > 0 ? `S${this.epi.season.number}` : null
      const type = this.epi.type === 'trailer' || this.epi.type === 'bonus' ? this.epi.type.toUpperCase() : null
      // This logic is for handling when a trailer or a bonus episode is following a season it is spaced by a dash
      // Allows for these example combinations: S1 - TRAILER, S1:EP3, EP3, TRAILER
      let meta = ''
      if (s) meta += s
      if (s && ep && !type) meta += ':'
      if (ep !== null && !type) meta += ep
      if (s && type) meta += ' - '
      if (type) meta += type

      return meta
    },
    size () { return this.store.state.size }
  },
  watch: {
    'store.state.isPlaying' (val) {
      if (val) {
        this.setEpScrolling()
        this.setPodScrolling()
      } else {
        this.epScrolling = false
        this.podScrolling = false
      }
    },
    'store.state.episode' () {
      window.document.title = this.epiTitle
    }
  },
  methods: {
    setEpScrolling () {
      if (!this.$refs.epText || !this.$refs.epHeading) return
      if (this.$refs.epText.clientWidth >= this.$refs.epHeading.clientWidth) {
        this.epScrolling = true
      }
    },
    setPodScrolling () {
      if (!this.$refs.podText || !this.$refs.podHeading) return
      if (this.$refs.podText.clientWidth >= this.$refs.podHeading.clientWidth) {
        this.podScrolling = true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'node_modules/simplecast-styles/src/assets/styles/_core/colors.scss';
@import 'node_modules/simplecast-styles/src/assets/styles/_core/fonts.scss';
@import '../assets/styles/breakpoints.scss';

@keyframes ticker {
  from {transform: translate(0)}
  to {transform: translate(-100%)}
}
@keyframes ticker2 {
  from {transform: translate(100%)}
  to {transform: translate(0)}
}

.meta-info {
  align-self: flex-start;
  width: 100%;

  &-explicit {
    background-color: $black-trans-500;
    height: 13px;
    position: absolute;
    right: 0;
    text-align: center;
    width: 13px;

    svg {
      fill: $white-trans-600;
    }
  }
  h1,
  h2 {
    margin: 0 50px 0 0;
    overflow: hidden;
    position: relative;

    .meta-info-static {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 0;
      white-space: nowrap;
    }
    .meta-info-ticker {
      opacity: 0;
      padding-right: 30px;
      position: absolute;
      top: 0;
      left: 0;
      white-space: nowrap;
    }
    &.scrolling {
      .meta-info-static {
        opacity: 0;
      }
      .meta-info-ticker {
        animation-duration: 10s;
        animation-name: ticker;
        animation-iteration-count: 1;
        animation-timing-function: linear;
        opacity: 1;
      }
      .meta-info-ticket-secondary {
        animation-duration: 10s;
        animation-name: ticker2;
        animation-iteration-count: 1;
        animation-timing-function: linear;
      }
    }
  }
  h1 {
    color: $black-trans-800;
    display: flex;

    @include font-xl-embed(700);
    font-size: 14px;
    line-height: 17px;
    @media (min-width: $bp_xs) {
      font-size: 18px;
      line-height: 20px;
    }
    @media (min-width: $bp_sm) {
      font-size: 20px;
      line-height: 24px;
    }
  }
  &.dark h1 {
    color: $white-trans-800;
  }
  h2 {
    color: $black-trans-500;
    display: flex;

    @include font-s();
    font-size: 11px;
    @media (min-width: $bp_sm) {
      font-size: 12px;
    }
  }
  &.dark h2 {
    color: $white-trans-500;
  }
  &.size-mini h1 {
    @include font-m-embed(700);
  }
}
.powered-by {
  @include font-xs();
  display: none;
  color: $black-trans-500;
  line-height: 1;
  a {
    color: $black-trans-500;
  }
  .dark & {
    color: $white-trans-500;
    a {
      color: $white-trans-500;
    }
  }
}
</style>
